import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AiOutlinePhone, AiOutlineReload } from 'react-icons/ai';
import { useLocation, useNavigate } from 'react-router-dom';
import myAxios from '../AxiosUrl';
import styles from './AICalling.module.css';
import AgentSettings from './AgentSettings';
import CallHistory from './CallHistory';
import MarketingCampaigns from './MarketingCampaigns';
import DoNotCall from './DoNotCall';

/**
 * AI Calling — seller page (/ai-calling), 2026-10-31. Tabs:
 *  - Balance & numbers: the call balance (bKash top-up, minimum ৳500), the numbers the super admin
 *    gave this store (one free with a paid package, its channels follow the package) with their line
 *    connection, prices and the balance history. bKash returns here with ?paymentID=…&status=…;
 *    the server checks the payment itself.
 *  - Shop & voice / Customer support / Order confirmation: the AI agent's settings, prompts and test
 *    calls (AgentSettings); support can be off, the AI, the softphone, or the AI then a person.
 *  - Marketing: campaigns to the shop's own customers (MarketingCampaigns).
 *  - Calls: every call with its conversation and recording (CallHistory).
 *  - Do-not-call: the shop's list (DoNotCall).
 */

const PACKAGE_NAMES = { startup: 'Startup', smallBusiness: 'Small Business', business: 'Business', enterprise: 'Enterprise', custom: 'Custom', trial: 'Trial' };
const TYPE_NAMES = { topup: 'Top-up (bKash)', adjust: 'Adjustment', rent: 'Number rent', call: 'Call', refund: 'Refund', sms: 'SMS' };
const QUICK = [500, 1000, 2000, 5000];
const TABS = [
    ['balance', 'Balance & numbers'],
    ['shop', 'Shop & voice'],
    ['support', 'Customer support'],
    ['orders', 'Order confirmation'],
    ['marketing', 'Marketing'],
    ['calls', 'Calls'],
    ['dnc', 'Do-not-call'],
];
const LINE = { registered: ['Connected', styles.pillGreen], registering: ['Connecting…', styles.pillGrey], failing: ['Not connected', styles.pillRed] };

function LineState({ status, numberId }) {
    if (!status) return <span className={styles.muted}>…</span>;
    if (!status.engineOnline) return <span className={styles.muted}>Service offline</span>;
    const l = (status.lines || []).find((x) => String(x.numberId) === String(numberId));
    const v = l ? LINE[l.state] || [l.state, styles.pillGrey] : ['Not connected', styles.pillGrey];
    return <span className={`${styles.pill} ${v[1]}`}>{v[0]}</span>;
}

const taka = (n) => `৳${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

function NumberStatus({ n }) {
    if (n.status === 'suspended') return <span className={`${styles.pill} ${styles.pillRed}`}>Suspended</span>;
    if (n.graceUntil) return <span className={`${styles.pill} ${styles.pillAmber}`}>Grace until {fmtDate(n.graceUntil)}</span>;
    return <span className={`${styles.pill} ${styles.pillGreen}`}>Active</span>;
}

function AICalling() {
    const location = useLocation();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [amount, setAmount] = useState(500);
    const [paying, setPaying] = useState(false);
    const [status, setStatus] = useState(null);
    const tab = TABS.some(([k]) => k === new URLSearchParams(location.search).get('tab')) ? new URLSearchParams(location.search).get('tab') : 'balance';
    const setTab = (k) => navigate(k === 'balance' ? '/ai-calling' : `/ai-calling?tab=${k}`, { replace: true });
    const checkedPayment = useRef(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [res, st] = await Promise.all([myAxios.get('call/overview'), myAxios.get('call/status').catch(() => null)]);
            setData(res.data);
            setStatus(st?.data || { engineOnline: false, lines: [] });
        } catch (err) {
            toast.error(errMsg(err, 'Could not load AI calling'));
        } finally {
            setLoading(false);
        }
    }, []);

    // Back from bKash: let the server check the payment, then clean the URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const paymentID = params.get('paymentID');
        if (!paymentID || checkedPayment.current) {
            if (!data) load();
            return;
        }
        checkedPayment.current = true;
        (async () => {
            try {
                const res = await myAxios.post('call/topup/execute', { paymentID, status: params.get('status') });
                toast.success(res.data?.already ? 'This payment is already in your balance' : `${taka(res.data?.amount)} added to your call balance`);
            } catch (err) {
                const msg = errMsg(err, 'The payment did not go through');
                if (err?.response?.status === 202) toast.info(msg);
                else toast.error(msg);
            } finally {
                navigate('/ai-calling', { replace: true });
                load();
            }
        })();
    }, [location.search, load, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

    const minTopup = data?.prices?.minTopupBdt || 500;

    const topUp = async () => {
        const amt = Math.round(Number(amount));
        if (!(amt >= minTopup)) return toast.error(`Minimum top-up is ${taka(minTopup)}`);
        try {
            setPaying(true);
            const res = await myAxios.post('call/topup/create', { amount: amt });
            if (res.data?.bkashURL) window.location.href = res.data.bkashURL;
            else toast.error('bKash did not open, please try again');
        } catch (err) {
            toast.error(errMsg(err, 'Could not start the payment'));
        } finally {
            setPaying(false);
        }
    };

    const pkg = data?.package;
    const free = data?.freeNumber;
    const freeNum = (data?.numbers || []).find((n) => n.free);
    const freeCapped = freeNum && (freeNum.activeChannels ?? freeNum.channels) < freeNum.channels;
    const numbers = data?.numbers || [];
    const prices = data?.prices || {};
    const problem = numbers.find((n) => n.status === 'suspended' || n.graceUntil);
    const hasNumber = numbers.some((n) => n.status === 'assigned');

    return (
        <div className={styles.container}>
            <ToastContainer position="top-right" autoClose={4000} />
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.headerIcon}><AiOutlinePhone /></div>
                    <div>
                        <h1 className={styles.title}>AI Calling</h1>
                        <p className={styles.subtitle}>The AI confirms your orders by phone and answers your customers' calls</p>
                    </div>
                </div>
                <button className={styles.btnSecondary} onClick={load} disabled={loading}>
                    <AiOutlineReload /> Refresh
                </button>
            </div>

            <div className={styles.tabs}>
                {TABS.map(([k, label]) => (
                    <button key={k} className={`${styles.tab} ${tab === k ? styles.tabActive : ''}`} onClick={() => setTab(k)}>{label}</button>
                ))}
            </div>

            {problem && (
                <div className={styles.warn}>
                    {problem.status === 'suspended'
                        ? `Number ${problem.number} is suspended (${problem.suspendedReason === 'rent_unpaid' ? 'rent not paid' : 'package expired'}). ${problem.suspendedReason === 'rent_unpaid' ? 'Top up your call balance' : 'Renew your package'} to turn it back on.`
                        : `Number ${problem.number}: ${problem.graceReason === 'rent' ? 'this month’s rent could not be taken' : 'your package has expired'}. It stops on ${fmtDate(problem.graceUntil)} unless you ${problem.graceReason === 'rent' ? 'top up your call balance' : 'renew your package'}.`}
                </div>
            )}

            {['shop', 'support', 'orders'].includes(tab) && data && (
                <AgentSettings key={tab} section={tab} hasNumber={hasNumber} balance={data.balance} status={status} onShowMissed={() => navigate('/ai-calling?tab=calls&filter=missed', { replace: true })} />
            )}
            {tab === 'marketing' && data && <MarketingCampaigns hasNumber={hasNumber} balance={data.balance} />}
            {tab === 'calls' && data && <CallHistory month={status?.month} initialFilter={new URLSearchParams(location.search).get('filter') || ''} openCallId={new URLSearchParams(location.search).get('call') || ''} />}
            {tab === 'dnc' && <DoNotCall />}

            {tab === 'balance' && <>
            <div className={styles.grid}>
                <div className={styles.card}>
                    <span className={styles.statLabel}>Call balance</span>
                    <div className={styles.statValue}>{loading && !data ? '…' : taka(data?.balance)}</div>
                    <div className={styles.statSub}>Calls and extra numbers are paid from this balance.</div>
                </div>
                <div className={styles.card}>
                    <span className={styles.statLabel}>Free number</span>
                    <div className={styles.statValue}>{free?.entitled ? `${free.channels} ch` : '—'}</div>
                    <div className={styles.statSub}>
                        {free?.entitled
                            ? free.has
                                ? freeCapped
                                    ? `Included with your ${PACKAGE_NAMES[pkg?.packageType] || ''} package. ${freeNum.activeChannels} works now; the rest open when the line is upgraded.`
                                    : `Included with your ${PACKAGE_NAMES[pkg?.packageType] || ''} package.`
                                : `Your ${PACKAGE_NAMES[pkg?.packageType] || ''} package includes 1 free number. Ask support to activate it.`
                            : 'Any paid package includes 1 free number.'}
                    </div>
                </div>
                <div className={styles.card}>
                    <span className={styles.statLabel}>Numbers</span>
                    <div className={styles.statValue}>{numbers.length}</div>
                    <div className={styles.statSub}>Extra numbers: {taka(prices.channelMonthlyBdt)} per channel per month.</div>
                </div>
            </div>

            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Top up with bKash</h2>
                </div>
                <div className={styles.chips}>
                    {QUICK.map((q) => (
                        <button key={q} className={`${styles.chip} ${Number(amount) === q ? styles.chipActive : ''}`} onClick={() => setAmount(q)}>
                            {taka(q)}
                        </button>
                    ))}
                </div>
                <div className={styles.row}>
                    <div style={{ maxWidth: 200, flex: '1 1 160px' }}>
                        <input className={styles.input} type="number" min={minTopup} step="100" value={amount} onChange={(e) => setAmount(e.target.value)} aria-label="Amount in taka" />
                    </div>
                    <button className={styles.btnPrimary} onClick={topUp} disabled={paying}>
                        {paying ? 'Opening bKash…' : 'Pay with bKash'}
                    </button>
                </div>
                <p className={styles.hint}>Minimum {taka(minTopup)}. The balance does not expire.</p>
            </div>

            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Your numbers</h2>
                </div>
                {numbers.length === 0 ? (
                    <div className={styles.empty}>No number yet. Our team sets up your number; contact support to get one.</div>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr><th>Number</th><th>Channels</th><th>Type</th><th>Status</th><th>Line</th><th>Rent</th></tr>
                            </thead>
                            <tbody>
                                {numbers.map((n) => (
                                    <tr key={n._id}>
                                        <td className={styles.num}><b>{n.number}</b>{n.label ? <div className={styles.muted}>{n.label}</div> : null}</td>
                                        <td>
                                            {n.activeChannels ?? n.channels} <span className={styles.muted}>at once</span>
                                            {(n.activeChannels ?? n.channels) < n.channels && (
                                                <div className={styles.muted} style={{ maxWidth: 220, whiteSpace: 'normal' }}>
                                                    {n.free ? 'Your package includes' : 'You have'} {n.channels}; the rest open when the line is upgraded.
                                                </div>
                                            )}
                                        </td>
                                        <td>{n.free ? <span className={`${styles.pill} ${styles.pillBlue}`}>Free with package</span> : <span className={`${styles.pill} ${styles.pillGrey}`}>Paid</span>}</td>
                                        <td><NumberStatus n={n} /></td>
                                        <td>{n.status === 'assigned' ? <LineState status={status} numberId={n._id} /> : '—'}</td>
                                        <td className={styles.num}>{n.free ? '—' : <>{taka(n.monthlyRent)}/month<div className={styles.muted}>paid until {fmtDate(n.rentPaidUntil)}</div></>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <p className={styles.hint}>Channels = how many calls can run at the same time on the number. Customers can call this number; the AI answers (turn it off in AI agent).</p>
            </div>

            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Prices</h2>
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <tbody>
                            <tr>
                                <td>AI call</td>
                                <td>
                                    The AI's real cost (Claude + Soniox, converted to taka) + phone line {taka(prices.linePerMinBdt ?? 0.5)} per minute
                                    {prices.aiMarkupPct > 0 ? ` + ${prices.aiMarkupPct}%` : ''} — charged by the second, only when the call is answered
                                </td>
                            </tr>
                            <tr><td>Softphone call</td><td>{taka(prices.softphonePerMinBdt ?? 0.5)} per minute, by the second</td></tr>
                            <tr><td>Free number</td><td>1 with any paid package — Startup {prices.packageChannels?.startup ?? 1} channel, Small Business {prices.packageChannels?.smallBusiness ?? 2}, Business {prices.packageChannels?.business ?? 4}, Enterprise {prices.packageChannels?.enterprise ?? 4}</td></tr>
                            <tr><td>Extra number</td><td>{taka(prices.channelMonthlyBdt)} per channel per month</td></tr>
                            <tr><td>Package expired / rent unpaid</td><td>The number keeps working for {prices.graceDays ?? 7} days, then it is suspended</td></tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Balance history</h2>
                {(data?.ledger || []).length === 0 ? (
                    <div className={styles.empty}>No transactions yet.</div>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead><tr><th>Date</th><th>Type</th><th>Amount</th><th>Balance</th><th>Note</th></tr></thead>
                            <tbody>
                                {data.ledger.map((l) => (
                                    <tr key={l._id}>
                                        <td className={styles.num}>{fmtDateTime(l.createdAt)}</td>
                                        <td>{TYPE_NAMES[l.type] || l.type}</td>
                                        <td className={`${styles.num} ${l.amount >= 0 ? styles.plus : styles.minus}`}>{l.amount >= 0 ? '+' : '−'}{taka(Math.abs(l.amount))}</td>
                                        <td className={styles.num}>{taka(l.balanceAfter)}</td>
                                        <td>{l.note}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            </>}
        </div>
    );
}

export default AICalling;
